import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { PageHeader, EmptyState, LoadingShell } from '@/components/ui-bits';
import { Building2, Plus, X, Save, Trash2, Search } from 'lucide-react';
import { toast } from 'sonner';

export default function CompaniesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '500' });
      if (q) params.set('q', q);
      const { data } = await api.get(`/companies?${params}`);
      setItems(data.companies || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      if (editing.id) {
        await api.patch(`/companies/${editing.id}`, editing);
        toast.success('Empresa actualizada');
      } else {
        await api.post('/companies', editing);
        toast.success('Empresa creada');
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const del = async (c) => {
    if (!confirm(`¿Eliminar ${c.name}?`)) return;
    try {
      await api.delete(`/companies/${c.id}`);
      toast.success('Eliminada');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  return (
    <div>
      <PageHeader
        title="Empresas"
        description="Cuentas B2B. Vincula leads y deals a una empresa."
        actions={(
          <button className="btn-primary" onClick={() => setEditing({ name: '', website: '', city: '', industry: '', email: '', phone: '', notes: '' })}>
            <Plus className="h-4 w-4" /> Nueva empresa
          </button>
        )}
      />
      <div className="surface p-3 mb-3 flex gap-2">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar empresa…" className="input pl-9" />
        </div>
      </div>
      {loading ? <LoadingShell /> : items.length === 0 ? (
        <EmptyState icon={Building2} title="Sin empresas" description="Crea la primera cuenta." cta={<button className="btn-primary" onClick={() => setEditing({ name: '' })}>Crear</button>} />
      ) : (
        <div className="surface overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground border-b border-border">
                <th className="px-3 py-2">Nombre</th>
                <th className="px-3 py-2">Ciudad</th>
                <th className="px-3 py-2">Industria</th>
                <th className="px-3 py-2">Contacto</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-b border-border/60 hover:bg-accent/40">
                  <td className="px-3 py-2 font-medium">
                    <Link to={`/app/companies/${c.id}`} className="hover:underline">{c.name}</Link>
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{c.city || '—'}</td>
                  <td className="px-3 py-2 text-muted-foreground">{c.industry || '—'}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{c.email || c.phone || '—'}</td>
                  <td className="px-3 py-2 text-right">
                    <button className="btn-ghost h-8 px-2 text-xs" onClick={() => setEditing(c)}>Editar</button>
                    <button className="btn-ghost h-8 w-8 p-0 text-[hsl(var(--danger))]" onClick={() => del(c)}><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setEditing(null)}>
          <div className="surface w-full max-w-lg p-6 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center">
              <h2 className="font-display text-lg font-semibold">{editing.id ? 'Editar' : 'Nueva'} empresa</h2>
              <button className="btn-ghost h-8 w-8 p-0" onClick={() => setEditing(null)}><X className="h-4 w-4" /></button>
            </div>
            <label className="block text-xs">Nombre *<input className="input mt-1" value={editing.name || ''} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></label>
            <div className="grid grid-cols-2 gap-2">
              <label className="block text-xs">Ciudad<input className="input mt-1" value={editing.city || ''} onChange={(e) => setEditing({ ...editing, city: e.target.value })} /></label>
              <label className="block text-xs">Industria<input className="input mt-1" value={editing.industry || ''} onChange={(e) => setEditing({ ...editing, industry: e.target.value })} /></label>
            </div>
            <label className="block text-xs">Web<input className="input mt-1" value={editing.website || ''} onChange={(e) => setEditing({ ...editing, website: e.target.value })} /></label>
            <div className="grid grid-cols-2 gap-2">
              <label className="block text-xs">Email<input className="input mt-1" value={editing.email || ''} onChange={(e) => setEditing({ ...editing, email: e.target.value })} /></label>
              <label className="block text-xs">Teléfono<input className="input mt-1" value={editing.phone || ''} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} /></label>
            </div>
            <label className="block text-xs">Notas<textarea className="input mt-1" value={editing.notes || ''} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} /></label>
            <div className="flex justify-end gap-2">
              <button className="btn-ghost" onClick={() => setEditing(null)}>Cancelar</button>
              <button className="btn-primary" onClick={save} disabled={!editing.name?.trim()}><Save className="h-4 w-4" /> Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
